import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ClientPurchaseOrderService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;
  clientConfig :any = environment.clientConfig;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  private readonly _purchaseOrders = new BehaviorSubject<any>(<any>[]);
  public purchaseOrders$ = this._purchaseOrders.asObservable();

  private readonly _purchaseOrdersStats = new BehaviorSubject<any>(<any>[]);
  public purchaseOrdersStats$ = this._purchaseOrdersStats.asObservable();

  private readonly _activeRecord = new BehaviorSubject<any>(<any>{});
  public activeRecord$ = this._activeRecord.asObservable();

  public gettingRecords :boolean = false;
  public gettingRecordsStats :boolean = false;

  private readonly _invoiceStats = new BehaviorSubject<any>(<any>[]);
  public invoiceStats$ = this._invoiceStats.asObservable();

  private getSub :Subscription;
  private getStatsSub :Subscription;
  private getBalanceSub :Subscription;

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  constructor(
    private translate :TranslateService,
    private http :HttpClient,
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
  }

  getAsync(params :Record<string, any> = {}) :Observable<any> {
    return this.http.get(`${this.url}/clients/purchase_orders`, {
      params: params,
    });
  }

  getQuickbooksInvoiceUrl(purchaseOrderId :number) :Observable<any> {
    return this.http.get(
      `${this.url}/clients/purchase_orders/${purchaseOrderId}/quickbooks-url`,
    );
  }

  getStatsAsync(params :Record<string, any> = {}) :Observable<any> {
    return this.http.get(`${this.url}/clients/purchase_orders/stats`, {
      params,
    });
  }

  getBalanceAsync(params :Record<string, any> = {}) :Observable<any> {
    return this.http.get(`${this.url}/clients/purchase_orders/balance`, {
      params: params,
    });
  }

  get(params) {
    this.gettingRecords = true;
    if (this.getSub) {
      this.getSub.unsubscribe();
    }
    this.getSub = this.http
      .get(`${this.url}/clients/purchase_orders`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._purchaseOrders.next(resp);
      });

    return this.getSub;
  }

  getStats(params) {
    this.gettingRecordsStats = true;
    if (this.getStatsSub) {
      this.getStatsSub.unsubscribe();
    }
    this.getStatsSub = this.http
      .get(`${this.url}/clients/purchase_orders/stats`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecordsStats = false;
        this._purchaseOrdersStats.next(resp);
      });

    return this.getStatsSub;
  }

  getById(purchaseOrderId :number) :Observable<any> {
    return this.http.get(`${this.url}/clients/purchase_orders/${purchaseOrderId}`);
  }

  syncInvoiceToQuickBooksInvoice(invoiceId :number) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/purchase-orders/${invoiceId}/quickbooks-invoice-sync`,
      {},
    );
  }

  bulkSyncInvoiceToQuickBooksInvoice(payload :any) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/quickbooks-invoice-sync`,
      payload,
    );
  }

  syncInvoiceToEdi810(invoiceId :number) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/purchase-orders/${invoiceId}/edi-810-sync`,
      {},
    );
  }

  updatepurchaseOrder(purchaseOrderId :number, orderData :any) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/purchase_orders/${purchaseOrderId}`,
      orderData,
    );
  }

  createpurchaseOrder(orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/clients/purchase_orders`, orderData);
  }

  deletepurchaseOrder(purchaseOrderId :number) {
    return this.http
      .delete(`${this.url}/clients/purchase_orders/${purchaseOrderId}`)
      .pipe(
        tap(() => {
          const purchaseOrders :any = this._purchaseOrders.getValue();
          if (purchaseOrders && purchaseOrders.data) {
            const poIdx = purchaseOrders.data.findIndex(
              (row) => row.id === purchaseOrderId,
            );
            if (poIdx >= 0) {
              purchaseOrders.data.splice(poIdx, 1);
              this._purchaseOrders.next(purchaseOrders);
            }
          }
        }),
      );
  }

  updatePurchaseOrderStatus(id :number, status :string, payload ?:any) {
    const requestPayload :any = payload ? payload : {};

    return this.http
      .put(
        `${this.url}/clients/purchase_orders/${id}/status/${status}`,
        requestPayload,
      )
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  resetPo(id :number) {
    return this.http.post(`${this.url}/clients/purchase_orders/${id}/reset-po`, []);
  }

  approvePo(poId :number) :Observable<any> {
    return this.http
      .post(`${this.url}/clients/purchase_orders/${poId}/approve-po`, [])
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  rejectPo(poId :number) :Observable<any> {
    return this.http
      .post(`${this.url}/clients/purchase_orders/${poId}/reject-po`, [])
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  tryUpdateOrAddResp(resp) {
    const purchaseOrders :any = this._purchaseOrders.getValue();
    if (!purchaseOrders || !purchaseOrders.data) {
      return;
    }
    const poIdx = purchaseOrders.data.findIndex((row) => row.id === resp.id);

    if (poIdx === -1) {
      purchaseOrders.data.splice(0, 0, resp);
    } else if (resp.order_status === 'VOID') {
      purchaseOrders.data.splice(poIdx, 1);
    } else {
      purchaseOrders.data[poIdx] = resp;
    }

    this._purchaseOrders.next(purchaseOrders);
  }

  public exportRFQtoPDF(purchaseOrder :any) {
    // const logoUri = await this.getImgDataUrl('/assets/img/logo-print.png');
    this.getImgDataUrlValue(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.creatPDF(purchaseOrder, imgUri);
      },
    );
  }

  creatPDF(purchaseOrder, logoUri) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'Item', bold: true },
        { text: 'Quantity', bold: true },
        { text: 'Unit Price', bold: true },
        { text: 'Sub Total', bold: true },
        { text: 'Discount', bold: true },
        { text: 'Line Total', bold: true },
      ],
    ];

    purchaseOrder.line_items.forEach((item) => {
      const subTotal :number = item.quantity * item.unit_price + item.markup_amount;
      const netTotal :number = subTotal - item.discount_amount;
      const data = [
        { text: '[' + item.sku + '] ' + item.product_name },
        { text: item.quantity, alignment: 'right' },
        {
          text: this.curPipe.transform(
            item.unit_price + item.markup_amount_per_unit,
          ),
          alignment: 'right',
        },
        { text: this.curPipe.transform(subTotal), alignment: 'right' },
        {
          text: this.curPipe.transform(item.discount_amount),
          alignment: 'right',
        },
        { text: this.curPipe.transform(netTotal), alignment: 'right' },
      ];
      tableData.push(data);
    });

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Purchase Order',
              alignment: 'right',
              style: 'header',
            },
            {
              text:
                'Order Date: ' +
                this.datePipe.transform(purchaseOrder.order_date, 'mediumDate'),
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    const footerContent = [
      {
        text: 'Total: ' + this.curPipe.transform(purchaseOrder.net_total_amount),
        alignment: 'right',
        style: 'footerContent',
      },
    ];

    let shipping_address = '';

    if (purchaseOrder.delivery_type == 'local_delivery') {
      shipping_address = 'Local Delivery';
    } else {
      shipping_address = [
        purchaseOrder.ship_address,
        purchaseOrder.ship_city,
        purchaseOrder.ship_state,
        purchaseOrder.ship_country,
        purchaseOrder.ship_zip,
      ].join(', ');
    }

    const tableContent :any[] = [
      {
        text: 'for: ' + purchaseOrder.client.business_name,
        alignment: 'left',
        style: 'header',
      },
      {
        text: 'Shipping Address: ' + shipping_address,
        style: 'subheader',
      },
      {
        text:
          'Public Note: ' +
          (purchaseOrder.public_note ? purchaseOrder.public_note : 'N/A'),
        style: 'subheader',
      },
      {
        style: 'table',
        table: {
          widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        logo: {
          fillColor: '#ee0000',
        },
        footerContent: {
          fontSize: 12,
          bold: true,
        },
        category: {
          fontSize: 14,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 30, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 12,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('ClientPurchaseOrder.pdf');
  }

  getImgDataUrlValue(imgUrl :string, callback) {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', imgUrl);
    xhr.responseType = 'blob';
    xhr.send();
  }

  search(params :any) :Observable<any> {
    return this.http.get(`${this.url}/clients/purchase_orders/search`, {
      params: params,
    });
  }

  addPayment(poId :number, payload) {
    return this.http.post(`${this.url}/payments/${poId}`, payload).pipe(
      tap((resp :any) => {
        this.tryUpdateOrAddResp(resp);
      }),
    );
  }

  deletePayment(paymentId :number, poId :number) {
    return this.http
      .delete(`${this.url}/payments/${paymentId}/delete/${poId}`)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  setActiveById(id :number) {
    this.gettingRecords = true;

    const record :any = this._purchaseOrders.getValue();
    if (record.data) {
      const po = record.data.find((row) => {
        return row.id === id;
      });
      if (po) {
        this.setActive(po);

        return;
      }
    }
    this.getById(id).subscribe((po) => {
      this.setActive(po);
    });
  }

  setActive(po :any) {
    this.gettingRecords = false;

    this._activeRecord.next(po);
  }

  getActive() {
    return this._activeRecord.getValue();
  }

  editPublicNote(clientPurchaseOrderId :number, payload :any) :Observable<any> {
    return this.http.put(
      `${this.urlV2}/b2b-portal/client-purchase-orders/${clientPurchaseOrderId}/public-note`,
      payload,
    );
  }

  /**
   * Send purchase order to contacts
   * @param {number} businessAcountId - Business account / client id
   * @param {number} clientPurchaseOrderId - Purchase Order id
   * @param {any} params
   * @return {Observable}
   */
  sendPurchaseOrderToContacts(
    businessAcountId :number,
    clientPurchaseOrderId :number,
    payload :any,
  ) :Observable<any> {
    return this.http.post(
      `${this.urlV2}/b2b-portal/business-accounts/${businessAcountId}/client-purchase-orders/${clientPurchaseOrderId}/send`,
      payload,
    );
  }

  getInvoiceBalance(params :any) :Subscription {
    this.gettingRecords = true;
    if (this.getBalanceSub) {
      this.getBalanceSub.unsubscribe();
    }
    this.getBalanceSub = this.http
      .get(`${this.url}/clients/purchase_orders/balance`, { params: params })
      .subscribe((res :any) => {
        this.gettingRecords = false;
        this._invoiceStats.next(res);
      });

    return this.getBalanceSub;
  }

  createLineItem(clientPurchaseOrderId :number, payload :any) :Observable<any> {
    return this.http.post(
      `${this.urlV2}/b2b-portal/client-purchase-orders/${clientPurchaseOrderId}/line-items`,
      payload,
    );
  }

  updateLineItem(
    clientPurchaseOrderId :number,
    lineItemId :number,
    payload :any,
  ) :Observable<any> {
    return this.http.put(
      `${this.urlV2}/b2b-portal/client-purchase-orders/${clientPurchaseOrderId}/line-items/${lineItemId}`,
      payload,
    );
  }

  removeLineItem(lineItemId :number) :Observable<any> {
    return this.http.delete(
      `${this.urlV2}/b2b-portal/client-purchase-orders/line-items/${lineItemId}`,
    );
  }

  bulkUpdateLineItems(
    clientPurchaseOrderId :number,
    payload :any,
  ) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/purchase-orders/${clientPurchaseOrderId}/line-items-bulk-update`,
      payload,
    );
  }

  exportListtoCSV(paymentReports :any) :void {
    const fileName :string = 'Client Invoice Payment Report';
    // Records sheet
    const jsonData :any = [];
    let balanceDue :number = 0;
    paymentReports.forEach((dr) => {
      if (dr.payments?.length) {
        let totalPaidAmount = 0;
        dr.payments.forEach((clientInvoice :any) => {
          totalPaidAmount += clientInvoice.payment_amount;
          balanceDue = dr.net_total_amount - totalPaidAmount;

          jsonData.push({
            Client: dr.client.business_name,
            'Payment Date': this.datePipe.transform(
              clientInvoice.updated_at,
              'MM/dd/yyyy',
            ),
            'Payment Amount': clientInvoice.payment_amount,
            'Payment Type': clientInvoice.payment_type,
            'Invoice #': clientInvoice.po_id.toString(),
            'Invoice Status': dr.order_status,
            'Balance Due': balanceDue.toFixed(2),
            'Invoice Total': dr.net_total_amount.toFixed(2),
          });
        });
      }

      let totalDiscountCreditMemo :number = 0;
      let isCreditMemo :boolean = false;

      if (dr?.discounts?.[0]) {
        dr.discounts.forEach((discount :any) => {
          if (discount.discount_code === 'CREDIT_MEMO') {
            totalDiscountCreditMemo += discount.discount_amount;
            isCreditMemo = true;
          }
        });
      }

      if (isCreditMemo && dr.payment_status === 1 && dr.order_status !== 'DRAFT') {
        jsonData.push({
          Client: dr.client.business_name,
          'Payment Date': this.datePipe.transform(dr.order_date, 'MM/dd/yyyy'),
          'Payment Amount': totalDiscountCreditMemo.toFixed(2),
          'Payment Type': 'credit memo',
          'Invoice #': dr.id.toString(),
          'Invoice Status': dr.order_status,
          'Balance Due': 0,
          'Invoice Total': totalDiscountCreditMemo.toFixed(2),
        });
      }
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }
}
